import { HTTP_STATUS_CODES } from '@showbie-socrative/socrative-utils';
import { z } from 'zod/v4';

import localAxios from '../localAxios';
import { orderSchema, questionSchema } from '../responses';
import { importFromDocOrTextURL } from './urls';

interface ImportFromDocParams {
  quizId: number;
  workspaceId?: string;
  workspaceQuizId?: string;
  insertionOrder: number;
  fileKey: string;
  includeExplanation: boolean;
}

const questionResponseSchema = z.object({
  questions: z.array(questionSchema),
  orders: z.array(orderSchema),
});

const asyncImportResponseSchema = z.object({
  async: z.literal(true),
  requestId: z.string(),
  fileKey: z.string(),
  quizId: z.number(),
  insertionOrder: z.number(),
  message: z.string(),
  elapsedTime: z.number(),
});

const syncImportResponseSchema = z.object({
  result: questionResponseSchema,
  elapsedTime: z.number(),
  version: z.string(),
  usage: z.object({
    promptTokens: z.number(),
    completionTokens: z.number(),
    totalTokens: z.number(),
  }),
});

const importFromDocResponseSchema = z.union([
  asyncImportResponseSchema,
  syncImportResponseSchema,
]);
export type ImportFromDocResponse = z.infer<typeof importFromDocResponseSchema>;

export async function importFromDoc({
  quizId,
  workspaceId,
  workspaceQuizId,
  insertionOrder,
  fileKey,
  includeExplanation,
}: ImportFromDocParams) {
  const importURL = importFromDocOrTextURL(quizId);

  const response = await localAxios.post(importURL.href, {
    workspaceId,
    workspaceQuizId,
    insertionOrder,
    fileKey,
    includeExplanation,
  });

  const parsedData = importFromDocResponseSchema.parse(response.data);

  // Handle async response - polling will handle the final result
  if (
    'async' in parsedData &&
    parsedData.async &&
    response.status === HTTP_STATUS_CODES.HTTP_202_ACCEPTED
  ) {
    return {
      questions: [],
      orders: [],
      elapsedTime: parsedData.elapsedTime,
      version: 'async',
      async: true,
      requestId: parsedData.requestId,
    };
  }

  // Handle sync response

  const syncData = parsedData as z.infer<typeof syncImportResponseSchema>;
  const {
    result: { questions, orders },
    version,
    elapsedTime,
  } = syncData;

  // TODO: Implement Analytics. (Either here or in the mutation hook).

  return {
    questions,
    orders,
    elapsedTime,
    version,
  };
}
